import { AuthProvider } from "./Components/utils/auth";
import { CookiesProvider } from "react-cookie";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Login, handleRedirect } from "./Components/pages/Login";
import { Dashboard } from "./Components/pages/Dashboard/Dashboard";
import { Sites } from "./Components/pages/Sites/SitesPage/Sites";
import { AddSite } from "./Components/pages/AddSite/AddSite";
import { AddCamera } from "./Components/pages/AddCamera/AddCamera";
import { CameraAutoAdd } from "./Components/pages/CameraAutoAdd/CameraAutoAdd";
import { OnvifScan } from "./Components/pages/OnvifScan/OnvifScan";
import { OnvifDiscoveryOnboarding } from "./Components/pages/OnvifScan/OnvifDiscoveryOnboarding";
import { AddSchedule } from "./Components/pages/AddSchedule/AddSchedule";
import { RequireAuth } from "./Components/utils/RequireAuth";
import { GroupUsers } from "./Components/pages/GroupUsers/GroupUsers";
import { TalkDownMessages } from "./Components/pages/TalkDownMessages/TalkDownMessages";
import { Analytics } from "./Components/pages/Analytics/Analytics";
import { Callback } from "./Components/pages/Callback";
import { TestPage } from "./Components/pages/TestPage";
import { StreamTest } from "./Components/pages/StreamTest/StreamTest";
import { EagleEyeCallback } from "./Components/pages/EagleEyeCallback";
import { SiteAbout } from "./Components/pages/Sites/SiteAboutPage/SiteAbout";
import { PageNotFound } from "./Components/pages/PageNotFound";
import { ResourceNotFound } from "./Components/pages/ResourceNotFound";
import { NoPermissionsPage } from "./Components/pages/NoPermissionsPage";
import { ProductRequirements } from "./Components/pages/ProductRequirements";
import { MFASetup } from "./Components/pages/MFASetup/MFASetup";
import { EditIgnoreZones } from "./Components/pages/EditIgnoreZones/EditIgnoreZones";
import { NewIgnoreZones } from "./Components/pages/NewIgnoreZones/NewIgnoreZones";
import { EditIMetadataTagZones } from "./Components/pages/EditIMetadataTagZones/EditMetaDataTagZones";
import { EditLineCrossings } from "./Components/pages/EditLineCrossings/EditLineCrossings";
import { EditCamera } from "./Components/pages/EditCamera/EditCamera";
import { CameraCheck } from "./Components/pages/CameraCheck/CameraCheck";
import { Home } from "./Components/pages/Home/Home";
import { Clips } from "./Components/pages/Clips/Clips";
import { Patrols } from "./Components/pages/Patrols/Patrols";
import { Calendars } from "./Components/pages/Calendars/Calendars";
import "./index.css";
import Alerts from "./Components/pages/Alerts/Alerts";
import { SiteVideoWallPage } from "./Components/pages/Alerts/SiteVideoWall/SiteVideoWallPage";
import { HealthMonitoring } from "./Components/pages/HealthMonitoring/HealthMonitoring";
import { ActionLogs } from "./Components/pages/ActionLogs/ActionLogs";
import { WireGuardTunnels } from "./Components/pages/WireGuardTunnels/WireGuardTunnels";
import { Troubleshooting } from "./Components/pages/Troubleshooting/Troubleshooting";
import { VpnStatus } from "./Components/pages/VpnStatus/VpnStatus";
import { canSeeVpnStatus } from "./Components/pages/VpnStatus/vpnStatusUtils";
import IconButton from "@mui/material/IconButton";
import { NoInternetPage } from "./Components/pages/NoInternetPage"; // Import the new page
import CloseIcon from "@mui/icons-material/Close";
import { useAppStore } from "./appStore";
import { useClipsStore } from "./Components/pages/Clips/ClipsStore/clipsStore";
import { useFeatureFlag } from "./hooks/useFeatureFlag"; // Import the custom hook
import { cacheValidator } from "./Components/utils/cacheValidator";
import { authDebugger } from "./Components/utils/authDebugger";
import { getCookie } from "./Components/utils/getCookie";

import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { CameraCheckResultScreen } from "./Components/pages/CameraCheck/CameraCheckResultScreen";
import BackdropLoadingScreen from "./Components/common/BackdropLoadingScreen";
import { ProductionModeBar } from "./Components/common/ProductionModeBar";
import { NavBar } from "./Components/common/NavBar";
import { ScreenGate } from "./Components/utils/ScreenGate";
import {
  canUseCameraUnitScan,
  canUseOnvifScan,
} from "./Components/utils/onvifScanAccess";

const App = (packageJson) => {
  localStorage.setItem("packageJsonVersion", packageJson.packageJson.version);

  const { flags, isFeatureEnabled } = useFeatureFlag();
  const viewEditMetadataTagZonesPage = flags.viewEditMetadataTagZonesPage;
  const showLineCrossings = flags.showLineCrossings;
  const showNewIgnoreZonesPage = flags.showNewIgnoreZonesPage;
  const showWireguardTunnelsPage = flags.showWireguardTunnelsPage;
  const showStreamTest = flags.showStreamTest;
  const showTalkDown = Boolean(flags.showTalkDown);
  // Defaults ON so the redesign environment shows the page without a
  // LaunchDarkly change; create `showTroubleshootingPage` = false to hide it.
  const showTroubleshootingPage = isFeatureEnabled(
    "showTroubleshootingPage",
    true
  );
  // ENG-2531: the VPN status page. Defaults ON like the Troubleshooter; set
  // `showVpnStatusPage` = false in LaunchDarkly to hide it. Users with no VPN
  // to show never get it (permission.vpn_status from user/info).
  const showVpnStatusPage = isFeatureEnabled("showVpnStatusPage", true);
  // Initialize cache validation on app startup
  React.useEffect(() => {
    cacheValidator.validateOnStartup();
    // Start periodic validation every 5 minutes
    cacheValidator.startPeriodicValidation(300000);

    // Initialize auth debugger for console access
    authDebugger.logDebugInfo();

    // Cleanup on unmount
    return () => {
      cacheValidator.stopPeriodicValidation();
    };
  }, []);

  const { userPermissions, getWireGuardOptions } = useAppStore();
  const { showFrontelTab } = useClipsStore();

  const [permissions, setPermissions] = React.useState({
    arm_disarm: false,
    add_cameras: false,
    edit_cameras: false,
    delete_cameras: false,
    manage_users: false,
    super_user: false,
    configure_wireguard_cameras: false,
    view_analytics: false,
    view_alerts: false,
    view_passwords: false,
    direct_camera_access: false,
  });

  const [navBarCollapsed, setNavBarCollapsed] = React.useState(false);

  // ENG-1816 / ENG-2000: routes for the View Only screens (Sites, Alerts,
  // Analytics, Health, Patrols, WireGuard) wrap their existing gate's positive
  // branch in this, so a screen missing from the user's `visible_screens`
  // redirects to their landing route instead of rendering. Empty list = View
  // All = every route as today.
  const screenGateProps = {
    permission: userPermissions,
    navBarCollapsed,
    setNavBarCollapsed,
    landingOptions: { wireguardPageEnabled: Boolean(showWireguardTunnelsPage) },
  };

  const [cameraCheckFinsihedAlertOpen, setCameraCheckFinsihedAlertOpen] =
    React.useState(false);

  const [cameraCheckResultsDialogOpen, setCameraCheckResultsDialogOpen] =
    React.useState(false);

  const [goodCameras, setGoodCameras] = React.useState([]);

  const [connectivityCameras, setConnectivityCameras] = React.useState([]);

  const [lowResolutionCameras, setLowResolutionCameras] = React.useState([]);

  const [cameraCheckResultDownloadURL, setCameraCheckResultDownloadURL] =
    React.useState("");

  const navigate = useNavigate();
  const location = useLocation();

  // when a user attempts to go to any page before login save the page in local storage as redirect url
  React.useEffect(() => {
    if (!window.location.pathname.includes("callback")) {
      localStorage.setItem("redirectUrl", window.location.pathname);
    }
    if (!window.location.pathname.includes("callback")) {
      if (window.location.pathname.includes("login")) {
        localStorage.setItem("redirectUrl", "/dashboard");
      }
      //else set the local storage redirect url to whatever is after the first backslash in the url. So if the url pathname was "http://localhost:1234/sites/aboutsite/14483" then it would return "/sites/aboutsite/14483"
      else {
        localStorage.setItem("redirectUrl", window.location.pathname);
      }
    }
  }, []);

  const {
    backdropLoadingScreenRefreshTokenOn,
    refreshingToken,
    setBackdropLoadingScreenRefreshTokenOn,
    dateMMDDYYYY,
    updateUserStorageDateFormat,
    setUserPermissions,
  } = useAppStore();

  React.useEffect(() => {
    // Check authentication using cookies instead of localStorage
    const token = document.cookie.includes("token=");
    const refreshToken = document.cookie.includes("token_secret=");

    if (token || refreshToken) {
      const defaultPermissions = {
        arm_disarm: false,
        add_cameras: false,
        edit_cameras: false,
        delete_cameras: false,
        manage_users: false,
        super_user: false,
        configure_wireguard_cameras: false,
        view_analytics: false,
        view_alerts: false,
        view_passwords: false,
        direct_camera_access: false,
      };
      let userPermissions;
      try {
        const raw = localStorage.getItem("userPermissions");
        userPermissions = raw ? JSON.parse(raw) : null;
      } catch (e) {
        userPermissions = null;
      }
      const safePermissions = {
        ...defaultPermissions,
        ...(userPermissions || {}),
      };
      setUserPermissions(safePermissions);
      setPermissions(safePermissions);
    }
  }, []);

  React.useEffect(() => {
    if (refreshingToken) {
      setBackdropLoadingScreenRefreshTokenOn(true);
    }
  }, [refreshingToken]);

  React.useEffect(() => {
    const userStorage = JSON.parse(localStorage.getItem("userStorage"));
    if (userStorage) {
      updateUserStorageDateFormat(navigate, dateMMDDYYYY);
    }
  }, [dateMMDDYYYY]);

  // Defer WireGuard fetch until we're past the auth callback and have a token,
  // so we never hit the API without auth (avoids 403 and unnecessary failures).
  const hasFetchedWireGuardRef = React.useRef(false);
  React.useEffect(() => {
    if (location.pathname.includes("callback")) return;
    if (hasFetchedWireGuardRef.current) return;
    if (!getCookie("token")) return;
    hasFetchedWireGuardRef.current = true;
    getWireGuardOptions(navigate);
  }, [location.pathname, navigate]);

  const cameraCheckFinsihedAlert = (
    <>
      {cameraCheckFinsihedAlertOpen && (
        <div className="fixed top-0 left-0 w-full z-50">
          <Alert
            severity="success"
            onClose={() => setCameraCheckFinsihedAlertOpen(false)}
          >
            <p className="font-mulish">
              Camera check finished
              <Button
                type="button"
                onClick={() => {
                  localStorage.setItem("cameraCheckAlertOpen", "false");
                  setCameraCheckFinsihedAlertOpen(false);
                  setCameraCheckResultsDialogOpen(true);
                }}
                disabled={false}
                size="small"
              >
                CLICK HERE
              </Button>
              to see results.
            </p>
          </Alert>
        </div>
      )}
    </>
  );

  const cameraCheckResultsDialog = (
    <Dialog
      open={cameraCheckResultsDialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {"Camera Check Results"}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setCameraCheckResultsDialogOpen(false)}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <div className="w-[1000px]">
          <CameraCheckResultScreen
            goodCameras={goodCameras}
            connectivityCameras={connectivityCameras}
            lowResolutionCameras={lowResolutionCameras}
            cameraCheckResultDownloadURL={cameraCheckResultDownloadURL}
            navigate={navigate}
          />
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <AuthProvider>
      <CookiesProvider>
        <BackdropLoadingScreen
          openState={backdropLoadingScreenRefreshTokenOn}
        />
        <ProductionModeBar />
        <Routes>
          <Route path="/login" element={<Login />} />
          {showStreamTest && (
            <Route
              path="stream-test"
              element={
                <RequireAuth>
                  <StreamTest />
                </RequireAuth>
              }
            />
          )}
          <Route
            path="/"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <Home
                  permissions={permissions}
                  navBarCollapsed={navBarCollapsed}
                  setNavBarCollapsed={setNavBarCollapsed}
                />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {!userPermissions.patrol_only ? (
                  <ScreenGate screen="sites" {...screenGateProps}>
                    <Dashboard
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {!userPermissions.patrol_only ? (
                  <ScreenGate screen="sites" {...screenGateProps}>
                    <Sites
                      permissions={permissions}
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites_patrol"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.view_patrol ? (
                  <ScreenGate screen="patrols" {...screenGateProps}>
                    <Sites
                      permissions={permissions}
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="clips"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.ailink ||
                userPermissions.sentinel ||
                userPermissions.smtp ||
                showFrontelTab ? (
                  <ScreenGate screen="alerts" {...screenGateProps}>
                    <Clips
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <PageNotFound
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="patrols"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {/* view_patrol matches the NavBar links and the sites_patrol
                    route (and is already true for superusers); patrol_only
                    users have no view_patrol yet must keep this page. */}
                {userPermissions.view_patrol || userPermissions.patrol_only ? (
                  <ScreenGate screen="patrols" {...screenGateProps}>
                    <Patrols
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="patrols_test"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <Patrols
                  navBarCollapsed={navBarCollapsed}
                  setNavBarCollapsed={setNavBarCollapsed}
                />
              </RequireAuth>
            }
          />
          <Route
            path="sites/addsite"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {permissions.add_cameras ? (
                  <AddSite />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          {/* SMTP camera configuration moved to the Troubleshooter's SMTP Cameras tab. */}
          <Route
            path="/smtp"
            element={<Navigate to="/troubleshooter" replace />}
          />
          <Route
            path="sites/cameracheck"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.add_cameras ? (
                  <CameraCheck
                    setCameraCheckFinsihedAlertOpen={
                      setCameraCheckFinsihedAlertOpen
                    }
                    setGoodCameras={setGoodCameras}
                    setConnectivityCameras={setConnectivityCameras}
                    setLowResolutionCameras={setLowResolutionCameras}
                    setCameraCheckResultDownloadURL={
                      setCameraCheckResultDownloadURL
                    }
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/addCamera"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.add_cameras ? (
                  <AddCamera
                    setCameraCheckFinsihedAlertOpen={
                      setCameraCheckFinsihedAlertOpen
                    }
                    setGoodCameras={setGoodCameras}
                    setConnectivityCameras={setConnectivityCameras}
                    setLowResolutionCameras={setLowResolutionCameras}
                    setCameraCheckResultDownloadURL={
                      setCameraCheckResultDownloadURL
                    }
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/autoaddcamera"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.add_cameras ? (
                  <CameraAutoAdd
                    setCameraCheckFinsihedAlertOpen={
                      setCameraCheckFinsihedAlertOpen
                    }
                    setGoodCameras={setGoodCameras}
                    setConnectivityCameras={setConnectivityCameras}
                    setLowResolutionCameras={setLowResolutionCameras}
                    setCameraCheckResultDownloadURL={
                      setCameraCheckResultDownloadURL
                    }
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/:siteId/onvifscan"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {canUseOnvifScan(userPermissions) ? (
                  <OnvifScan
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/:siteId/unitscan"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {canUseCameraUnitScan(userPermissions) ? (
                  <OnvifScan
                    mode="update"
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/:siteId/onvifdiscovery"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {canUseOnvifScan(userPermissions) ? (
                  <OnvifDiscoveryOnboarding />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/addschedule"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.add_cameras ? (
                  <AddSchedule />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/addschedulesecret"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.add_cameras ? (
                  <AddSchedule />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/aboutsite/:siteid"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <ScreenGate screen="sites" {...screenGateProps}>
                  <SiteAbout
                    permissions={permissions}
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                </ScreenGate>
              </RequireAuth>
            }
          />
          {/* <Route
            path="sites/aboutsitesecret/:siteid"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <SiteAbout
                  permissions={permissions}
                  navBarCollapsed={navBarCollapsed}
                  setNavBarCollapsed={setNavBarCollapsed}
                />
              </RequireAuth>
            }
          /> */}
          <Route
            path="sites/:siteid/editcamera/:cameraid/editignorezones"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.edit_cameras ? (
                  <EditIgnoreZones
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          {showNewIgnoreZonesPage && (
            <Route
              path="sites/:siteid/editcamera/:cameraid/ignorezones"
              element={
                <RequireAuth>
                  {cameraCheckFinsihedAlert}
                  {cameraCheckResultsDialog}
                  {userPermissions.edit_cameras ? (
                    <NewIgnoreZones
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  ) : (
                    <NoPermissionsPage
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  )}
                </RequireAuth>
              }
            />
          )}
          <Route
            path="sites/:siteid/editcamera/:cameraid/editmetadatatagzones"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.edit_cameras &&
                viewEditMetadataTagZonesPage ? (
                  <EditIMetadataTagZones
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          {showLineCrossings && (
            <Route
              path="sites/:siteid/editcamera/:cameraid/editlinecrossings"
              element={
                <RequireAuth>
                  {cameraCheckFinsihedAlert}
                  {cameraCheckResultsDialog}
                  <EditLineCrossings
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                </RequireAuth>
              }
            />
          )}
          <Route
            path="sites/:siteid/editcamera/:cameraid"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <ScreenGate screen="sites" {...screenGateProps}>
                  <EditCamera
                    permissions={permissions}
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                </ScreenGate>
              </RequireAuth>
            }
          />
          <Route
            path="calendars"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.add_cameras ? (
                  <Calendars
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="actionlogs/:siteid"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {!userPermissions.patrol_only ? (
                  <ScreenGate screen="sites" {...screenGateProps}>
                    <ActionLogs
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="alerts&:alertsFilters"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.view_alerts ? (
                  <ScreenGate screen="alerts" {...screenGateProps}>
                    <Alerts
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="alerts"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {permissions.view_alerts ? (
                  <ScreenGate screen="alerts" {...screenGateProps}>
                    <Alerts
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="sites/:siteId/videowall"
            element={
              <RequireAuth>
                {permissions.view_alerts ? (
                  <ScreenGate screen="alerts" {...screenGateProps}>
                    <SiteVideoWallPage />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="analytics"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.view_analytics &&
                !userPermissions.patrol_only ? (
                  <ScreenGate screen="analytics" {...screenGateProps}>
                    <Analytics
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="analytics&:analyticsFilters"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.view_analytics &&
                !userPermissions.patrol_only ? (
                  <ScreenGate screen="analytics" {...screenGateProps}>
                    <Analytics
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          {/* <Route
            path="user"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <User
                  navBarCollapsed={navBarCollapsed}
                  setNavBarCollapsed={setNavBarCollapsed}
                />
              </RequireAuth>
            }
          /> */}
          <Route
            path="groupuser"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {userPermissions.manage_users ? (
                  // && !userPermissions.patrol_only
                  <GroupUsers
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="talkdown"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {showTalkDown && userPermissions.talk_down ? (
                  <TalkDownMessages
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="wireguardtunnels"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {showWireguardTunnelsPage ? (
                  <ScreenGate screen="wireguard" {...screenGateProps}>
                    <WireGuardTunnels
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="vpn"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {showVpnStatusPage && canSeeVpnStatus(userPermissions) ? (
                  <ScreenGate screen="wireguard" {...screenGateProps}>
                    <VpnStatus
                      navBarCollapsed={navBarCollapsed}
                      setNavBarCollapsed={setNavBarCollapsed}
                    />
                  </ScreenGate>
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="troubleshooter"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                {showTroubleshootingPage ? (
                  <Troubleshooting
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                ) : (
                  <NoPermissionsPage
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                )}
              </RequireAuth>
            }
          />
          <Route
            path="health"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <ScreenGate screen="health" {...screenGateProps}>
                  <HealthMonitoring
                    navBarCollapsed={navBarCollapsed}
                    setNavBarCollapsed={setNavBarCollapsed}
                  />
                </ScreenGate>
              </RequireAuth>
            }
          />
          <Route
            path="productrequirements"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <ProductRequirements
                  navBarCollapsed={navBarCollapsed}
                  setNavBarCollapsed={setNavBarCollapsed}
                />
              </RequireAuth>
            }
          />
          <Route
            path="callback"
            element={<Callback setPermissions={setPermissions} />}
          />
          <Route
            path="eecallback"
            element={
              <RequireAuth>
                <EagleEyeCallback />
              </RequireAuth>
            }
          />
          <Route
            path="MFA"
            element={
              // <RequireAuth>
              <MFASetup />
              // </RequireAuth>
            }
          />
          {/* Add the route for the No Internet page */}
          <Route path="/no-internet" element={<NoInternetPage />} />
          {/* Deleted / inaccessible site or camera — see navigateToResourceNotFound */}
          <Route
            path="/not-found"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <ResourceNotFound
                  navBarCollapsed={navBarCollapsed}
                  setNavBarCollapsed={setNavBarCollapsed}
                />
              </RequireAuth>
            }
          />
          <Route
            path="*"
            element={
              <RequireAuth>
                {cameraCheckFinsihedAlert}
                {cameraCheckResultsDialog}
                <PageNotFound
                  navBarCollapsed={navBarCollapsed}
                  setNavBarCollapsed={setNavBarCollapsed}
                />
              </RequireAuth>
            }
          />
        </Routes>
      </CookiesProvider>
    </AuthProvider>
  );
};

export default App;
